import { SectionCard } from '../components/SectionCard';
import { ItemLocationOrderSection } from '../components/ItemLocationOrderSection';
import { CostSummary } from '../components/CostSummary';
import { OrderInfo } from '../components/OrderInfo';
import { PageHeader } from '../components/PageHeader';
import { OrderFormLayout } from '../components/OrderFormLayout';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOrderFormContext } from './OrderFormContext';
import { TermsAndConditions } from './components/TermsAndConditions';
import { InfoFieldsSection } from './components/InfoFieldsSection';
import { AdditionalNote } from './components/AdditionalNote';
import { EmptyState } from './components/EmptyState';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FaExclamationTriangle } from 'react-icons/fa';
import i18n from '@/utils/i18n';

export function OrderForm() {
  const isMobile = useIsMobile();
  const {
    order,
    formControl,
    termsAndConditionsAccepted,
    fetchedFormInfo,
    formLoadingStatus,
  } = useOrderFormContext();

  const eventName = fetchedFormInfo?.event?.name;
  const eventDate = fetchedFormInfo?.event?.start_date;
  const hasNoItemLocations =
    formLoadingStatus === 'ready' && Object.keys(order).length === 0;

  // Show empty state for invalid link or fetch error
  if (
    formLoadingStatus === 'invalid-link' ||
    formLoadingStatus === 'fetch-error'
  ) {
    return <EmptyState />;
  }

  const isLoading =
    formLoadingStatus === 'initializing' || formLoadingStatus === 'loading';

  return (
    <div className="tw:container tw:flex tw:flex-col tw:gap-6 tw:p-6">
      <PageHeader
        title={
          eventName
            ? i18n.t('EventOrder:orderReusablesFor', { eventName })
            : i18n.t('EventOrder:orderReusables')
        }
        description={
          eventDate
            ? i18n.t('EventOrder:orderFormDescription', {
                eventName,
                date: Intl.DateTimeFormat().format(new Date(eventDate)),
              })
            : undefined
        }
        isLoading={isLoading}
      />
      <div className="tw:flex tw:flex-col tw:gap-4">
        {isMobile && <OrderInfo.Connected />}

        <OrderFormLayout
          isMobile={isMobile}
          leftColumn={
            termsAndConditionsAccepted ? (
              <>
                <InfoFieldsSection formControl={formControl} />
                {hasNoItemLocations ? (
                  <Alert
                    variant="destructive"
                    className="tw:border-amber-500/50 tw:text-amber-700 tw:dark:text-amber-400 tw:[&>svg]:text-amber-600"
                  >
                    <FaExclamationTriangle className="tw:h-4 tw:w-4" />
                    <AlertTitle>
                      {i18n.t('EventOrder:noItemLocationsTitle')}
                    </AlertTitle>
                    <AlertDescription>
                      {i18n.t('EventOrder:noItemLocationsDescription')}
                    </AlertDescription>
                  </Alert>
                ) : (
                  Object.values(order).map((iL) => (
                    <SectionCard
                      key={iL.itemLocationId}
                      title={iL.itemLocationName}
                    >
                      <ItemLocationOrderSection.Connected
                        itemLocationId={iL.itemLocationId}
                      />
                    </SectionCard>
                  ))
                )}
              </>
            ) : (
              <>
                <AdditionalNote />
                <TermsAndConditions />
              </>
            )
          }
          rightColumn={
            <>
              {!isMobile && <OrderInfo.Connected />}
              {termsAndConditionsAccepted && <CostSummary.Connected />}
            </>
          }
        />
      </div>
    </div>
  );
}
